import type { DiveObject } from "@divespots/contracts";
import { zurichDate } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, History } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { Button } from "../components/Button.tsx";
import { formatDate } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";

/** "Zuletzt gesehen: 27.9.2026 · 3 Meldungen", or "Zuletzt als fehlend gemeldet: …". */
export function SightingSummary({
  object,
}: {
  object: Pick<DiveObject, "lastSighting" | "sightingCount">;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const last = object.lastSighting;
  if (!last) {
    return <span className="ds-text-footnote text-label-secondary">{t("sighting.none")}</span>;
  }
  const date = formatDate(last.seenOn, locale);
  return (
    <span className="ds-text-footnote text-label-secondary" data-testid="sighting-summary">
      {last.present ? t("sighting.lastSeen", { date }) : t("sighting.lastMissing", { date })}
      {" · "}
      {t("sighting.count", { count: object.sightingCount })}
    </span>
  );
}

const MS_DAY = 86_400_000;

/** Report buttons (signed in only), an optional past day, and the history. */
export function SightingActions({ object, slug }: { object: DiveObject; slug: string }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const qc = useQueryClient();
  const uid = useId();
  const today = zurichDate();
  const oldest = zurichDate(new Date(Date.now() - 2 * 365 * MS_DAY + MS_DAY));
  const [day, setDay] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null);

  const history = useQuery({
    queryKey: ["sightings", object.id],
    queryFn: ({ signal }) => api.listSightings(object.id, signal).then((r) => r.sightings),
    enabled: showHistory,
  });

  const report = useMutation({
    mutationFn: (present: boolean) =>
      api.createSighting(object.id, { seenOn: day || today, present }),
    onSuccess: async () => {
      setNote({ text: t("sighting.thanks"), error: false });
      await qc.invalidateQueries({ queryKey: ["spot", slug] });
      await qc.invalidateQueries({ queryKey: ["sightings", object.id] });
    },
    onError: (e) =>
      setNote({
        text:
          isApiRequestError(e) && e.code === "bad_request"
            ? t("sighting.badDate")
            : isApiRequestError(e) && e.code === "rate_limited"
              ? t("sighting.tooMany")
              : t("admin.err.save"),
        error: true,
      }),
  });

  const name = object.name[locale] ?? Object.values(object.name)[0] ?? "";
  return (
    <div className="sighting">
      <div className="sighting__buttons">
        <Button
          variant="tinted"
          disabled={report.isPending}
          aria-label={t("sighting.seenNamed", { name })}
          icon={<Eye aria-hidden="true" strokeWidth={1.75} />}
          onClick={() => report.mutate(true)}
        >
          {day && day !== today
            ? t("sighting.seenOn", { date: formatDate(day, locale) })
            : t("sighting.seenToday")}
        </Button>
        <Button
          variant="tinted"
          disabled={report.isPending}
          aria-label={t("sighting.goneNamed", { name })}
          icon={<EyeOff aria-hidden="true" strokeWidth={1.75} />}
          onClick={() => report.mutate(false)}
        >
          {t("sighting.gone")}
        </Button>
        <Button
          variant="plain"
          aria-expanded={showHistory}
          icon={<History aria-hidden="true" strokeWidth={1.75} />}
          onClick={() => setShowHistory((v) => !v)}
        >
          {t("sighting.history")}
        </Button>
      </div>
      <label className="sighting__day ds-text-footnote" htmlFor={`${uid}-day`}>
        {t("sighting.otherDay")}
        <input
          id={`${uid}-day`}
          type="date"
          className="adm-input"
          min={oldest}
          max={today}
          value={day}
          onChange={(e) => setDay(e.target.value)}
        />
      </label>
      {note ? (
        <p
          className={note.error ? "adm-error ds-text-footnote m-0" : "adm-ok ds-text-footnote m-0"}
          role="status"
        >
          {note.text}
        </p>
      ) : null}
      {showHistory ? (
        <ul className="sighting__history ds-text-footnote" aria-label={t("sighting.history")}>
          {history.isPending ? <li>{t("common.loading")}</li> : null}
          {(history.data ?? []).map((s, i) => (
            <li key={`${s.seenOn}-${i}`}>
              {formatDate(s.seenOn, locale)} ·{" "}
              {s.present ? t("sighting.statusSeen") : t("sighting.statusGone")}
            </li>
          ))}
          {history.isSuccess && history.data.length === 0 ? <li>{t("sighting.none")}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}
